import Vue from 'vue';
import { HTTP } from '@/utils/httpCommonUtil';
import VueSimpleSpinner from "vue-simple-spinner";
import {UserRoleEnum} from '@/enums/userRolesEnum';
import InvoiceEventHub from '@/components/invoice/InvoiceEventHub';
import MaskedInput from 'vue-text-mask'
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { BallPulseLoader } from 'vue-loaders';
import {getRestParamErrorMessage} from "@/utils/serverErrorMessageUtil";

export default Vue.component('invoice-facility-billable', {
  name: 'InvoiceFacilityBillable',
  props: {
    'facilityId': { type: String },
    'specialtyId': { type: String },
    'invoiceDate': { type: String },
    'invoiceId': { type: [String, Number] },
    'mergedInvoiceId': { type: [String, Number] }
  },
  components: {
    VueSimpleSpinner,
    MaskedInput,
    BallPulseLoader
  },
  data () {
    return {
      loadingInitialData: false,
      cardExpanded: false,
      transactionData: null,
      facilityData: [] as any[],
      originalQuantity: 0,
      quantityErrorMessage: '',
      editingAdminFee: false,
      updatingData: false,
      quantityMask: createNumberMask({
        prefix: '',
        integerLimit: 2,
      }),
      rateMask: createNumberMask({
        prefix: '$',
        allowDecimal: true,
        decimalLimit: 2,
        integerLimit: 4,
        thousandsSeparatorSymbol: ''
      }),
    };
  },
  computed: {
    isPotential (): boolean {
      return !(Number(this.invoiceId) > 0);
    },
    isFacilityUser (): boolean {
      return this.$store.getters['user/hasOnlyRole'](UserRoleEnum.FACILITY_CONTACT);
    }
  },
  async created () {
    await this.expandCard();
    InvoiceEventHub.$on('INVOICE_CHANGE', this.dataFetchWrapper);
  },
  beforeDestroy: function () {
    InvoiceEventHub.$off('INVOICE_CHANGE', this.dataFetchWrapper);
  },
  methods: {
    // this comment copypasta is to denote that this function is to work around a leak caused by using anonymous functions in the event callback.
    // per this issue comment https://github.com/vuejs/vue-router/issues/1181#issuecomment-281084486
    // testing bears this out to be true and was causing unnecessary data fetches as well as bugs with incorrect route redirects
    dataFetchWrapper() {
      this.getTransactionInfo().then(() => {}).catch(() => '');
    },
    async expandCard (): Promise<void> {
      this.cardExpanded = !this.cardExpanded;
      if (this.cardExpanded) {
        await this.getTransactionInfo();
      }
    },
    async getTransactionInfo () {
      let showLoadingSpinnerTimeout = setTimeout(() => {
        this.loadingInitialData = true;
      }, 0);
      try {
        let parameters;
        if (this.invoiceId) {
          parameters = `invoiceId=${this.invoiceId}`;
        } else if (this.mergedInvoiceId) {
          parameters = `mergedInvoiceId=${this.mergedInvoiceId}`;
        } else if (this.facilityId && this.specialtyId && this.invoiceDate) {
          parameters = `facilityId=${this.facilityId}&specialtyId=${this.specialtyId}&invoiceDate=${this.invoiceDate}`;
        } else {
          throw new Error("Error retrieving Invoice Information");
        }
        const response = await HTTP.get('/api/invoices/transactions/billable?' + parameters) as any;
        clearTimeout(showLoadingSpinnerTimeout);
        this.loadingInitialData = false;
        if (!response.data || !response.data.data) {
          throw new Error("Error retrieving Invoice Information.");
        }
        const transactionData = response.data.data;
        transactionData.shifts.forEach((shift: any) => shift.isEditing = false);
        this.transactionData = transactionData;
        this.originalQuantity = transactionData.adminFeeQuantity;
        this.setFacilityUserData();
      } catch (err) {
        clearTimeout(showLoadingSpinnerTimeout);
        this.loadingInitialData = false;
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'An error occurred retrieving Invoice Information.'
        });
      }
    },
    setFacilityUserData() {
      if (!this.isFacilityUser || !this.transactionData) {
        return;
      }
      const data = new Map<number, {id: number, name: string, label: string, quantity: number, rate: number}>();
      for (const shift of (this.transactionData as any).shifts) {
        for (const transaction of shift.transactions) {
          let tran = data.get(transaction.transactionTypeId);
          if (!tran) {
            tran = {
              id: transaction.transactionTypeId,
              name: transaction.transactionTypeName,
              label: transaction.transactionTypeQuantityLabel,
              quantity: 0,
              rate: transaction.billRateCalculated
            };
            data.set(transaction.transactionTypeId, tran);
          }
          tran.quantity += transaction.quantity;
        }
      }
      this.facilityData = Array.from(data.values());
      this.facilityData.sort((a, b) => a.name.localeCompare(b.name));
    },
    toggleShift (shift: any) {
      shift.isEditing = !shift.isEditing;
    },

    browseToFacility(facilityId: number) {
      window.open("/facilities/" + facilityId, '_blank');
    },
    browseToShift (shiftId: number) {
      window.open("/shifts/" + shiftId, '_blank');
    },
    toggleAdminFee() {
      this.editingAdminFee = !this.editingAdminFee;
    },
    cancel() {
      (this.transactionData as any).adminFeeQuantity = this.originalQuantity;
      this.editingAdminFee = false;
    },
    async updateAdminFee() {
      try {
        this.quantityErrorMessage = '';
        this.updatingData = true;
        await this.sendUpdate();
        this.$notify({ group: 'main', type: 'success', title: 'Success', text: 'Updated Admin Fee' });
        await this.getTransactionInfo();
      } catch (err) {
        this.quantityErrorMessage = getRestParamErrorMessage(err, 'quantity');
        if (!this.quantityErrorMessage) {
          this.$notify({group: 'main', type: 'error', title: 'Error', text: 'An error occurred updating Admin Fee.'});
        }
      }
      this.updatingData = false;
    },
    async sendUpdate() {
      const body = {
        facilityId: this.facilityId,
        specialtyId: this.specialtyId,
        invoiceStartDate: this.invoiceDate,
        invoiceId: this.invoiceId,
        mergedInvoiceId: this.mergedInvoiceId,
        quantity: (this.transactionData as any).adminFeeQuantity
      };
      const response = await HTTP.put(`/api/invoices/admin-fees`, body) as any;
      if (!response.data || !response.data.data) {
        throw new Error('Error updating Admin Fee.');
      }
    }
  }
});



// WEBPACK FOOTER //
// ./src/components/invoice/cards/billable/InvoiceFacilityBillable.ts